<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import Icon from 'next/icon/Icon.vue';

const props = defineProps({
  userIds: { type: Array, default: () => [] },
  teamIds: { type: Array, default: () => [] },
  placeholder: { type: String, default: '' },
});

const emit = defineEmits(['update:userIds', 'update:teamIds']);

const store = useStore();
const { t } = useI18n();

const agents = computed(() => store.getters['agents/getAgents'] || []);
const teams = computed(() => store.getters['teams/getTeams'] || []);

const assigneeOptions = computed(() => [
  ...agents.value.map(user => ({
    key: `user-${user.id}`,
    type: 'user',
    id: user.id,
    name: user.name,
  })),
  ...teams.value.map(team => ({
    key: `team-${team.id}`,
    type: 'team',
    id: team.id,
    name: team.name,
  })),
]);

const selected = computed({
  get() {
    return assigneeOptions.value.filter(
      option =>
        (option.type === 'user' && props.userIds.includes(option.id)) ||
        (option.type === 'team' && props.teamIds.includes(option.id))
    );
  },
  set(options) {
    emit(
      'update:userIds',
      options.filter(option => option.type === 'user').map(option => option.id)
    );
    emit(
      'update:teamIds',
      options.filter(option => option.type === 'team').map(option => option.id)
    );
  },
});
</script>

<template>
  <multiselect
    v-model="selected"
    :options="assigneeOptions"
    track-by="key"
    label="name"
    multiple
    :close-on-select="false"
    :clear-on-select="false"
    hide-selected
    :placeholder="placeholder"
    :select-label="t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
    :deselect-label="t('FORMS.MULTISELECT.ENTER_TO_REMOVE')"
  >
    <template #option="{ option }">
      <span class="flex items-center gap-2">
        <Icon
          :icon="option.type === 'team' ? 'i-lucide-users' : 'i-lucide-user'"
          class="size-4 text-n-slate-10"
        />
        <span>{{ option.name }}</span>
      </span>
    </template>
  </multiselect>
</template>
